import React from "react";
import { Button } from "../../PSY/Button";
import { SingleValue } from "../../PSY/SingleValue";
import { CollateralInfo } from "../../Trove/PSY/CollateralInfo";
import { Decimal, Percent, UserTroveStatus } from "@liquity/lib-base";
import { useNavigate } from "react-router-dom";
import { CollateralLexicon, Lexicon } from "src/lexicon";
import { COIN } from "src/strings";
import { CollateralSurplusAction } from "../../CollateralSurplusAction";
import { CRITICAL_COLLATERAL_RATIO, MINIMUM_COLLATERAL_RATIO } from '@liquity/lib-base'
import { twMerge } from "tailwind-merge"
import { useLiquity } from "../../../hooks/LiquityContext"
import { getContractAddressByCollateralKey } from "@liquity/lib-ethers"

type Props = {
  collateralKey: string;
  collateralName: string;
  totalMinted: string;
  collateral: string;
  debt: string;
  collateralRatio?: Decimal;
  status: UserTroveStatus;
  hasSurplusCollateral: boolean;
  lexicon: CollateralLexicon;
  isRecoveryMode: boolean;
};

export const MyTrove: React.FC<Props> = ({
  collateralKey,
  collateralName,
  totalMinted,
  collateral,
  debt,
  collateralRatio,
  status,
  hasSurplusCollateral,
  lexicon,
  isRecoveryMode
}) => {
  const navigate = useNavigate();
  const { liquity: { connection } } = useLiquity();
  const { assetAddress } = getContractAddressByCollateralKey(connection, collateralKey);

  const collateralRatioPct = new Percent(collateralRatio ?? { toString: () => "N/A" });
  const crStyle = isRecoveryMode
    ? collateralRatio?.lt(CRITICAL_COLLATERAL_RATIO)
      ? "text-color-danger"
      : ""
    : collateralRatio?.lt(MINIMUM_COLLATERAL_RATIO)
      ? "text-color-danger"
      : collateralRatio?.gte(MINIMUM_COLLATERAL_RATIO) && collateralRatio?.lt(CRITICAL_COLLATERAL_RATIO)
        ? "text-color-warning"
        : ""

  return (
    <div className="bg-component flex flex-col md:flex-row justify-between py-5">
      <div className="flex flex-col w-full">
        <div className="flex flex-row justify-between">
          <CollateralInfo
            name={collateralName}
            iconUrl={lexicon.iconUrl}
            unit={lexicon.term ?? ""}
          />
        </div>
        <div className="flex flex-col gap-2 mt-4 md:flex-row md:w-4/5 justify-between">
          <SingleValue
            label="Total Minted"
            value={totalMinted}
            unit={COIN}
          />
          <SingleValue
            label="Your collateral"
            value={collateral}
            unit={lexicon.term}
          />
          <SingleValue
            label="Your debt"
            value={debt}
            unit={COIN}
          />
          <SingleValue
            label="Ratio"
            value={collateralRatioPct.prettify()}
            className={twMerge("typo-lead-14 text-white", `${crStyle}`)}
          />
        </div>
        {status === "closedByLiquidation" && (
          <div className="mt-2 text-style-emphasize">
            <div className="text-style-accent">
              Your Trove has been liquidated.
            </div>
            {hasSurplusCollateral
              ? "Please reclaim your remaining collateral before opening a new Trove."
              : "You can borrow SLSD by opening a Trove."
            }
          </div>
        )}
      </div>
      <div className="mt-3 md:mt-0">
        {(status === "closedByLiquidation" && hasSurplusCollateral) ? (
          <CollateralSurplusAction collateralKey={collateralKey} />
        ) : (
          <Button
            className="w-full md:w-32"
            onSubmit={() => {
              navigate(`products/${assetAddress}`);
            }}
          >
            {status === "closedByLiquidation" ? ("Borrow") : ("Manage")}
          </Button>
        )}
      </div>
    </div>
  );
};
